/**
 * Wie dicht die Figuren in der Reihe stehen (Schritt 3, Vorgriff auf Schritt 4).
 *
 * ⚠️ **Das ist eine Abkehr von `ARCHITEKTUR.md` 6.2**, wo steht: „Überlappen, Lücken und
 * Elemente am Rand sind konstruktiv ausgeschlossen." Auf Annas eigenem Produktfoto
 * (`annas-eventpainting.com/shop/p/minicollage`) stehen die Figuren **Schulter an
 * Schulter und überlappen leicht** — genau das macht aus einer Aufreihung eine
 * Komposition. Von Carolin am 24.09.2026 entschieden: die Figuren sollen schon aus dem
 * Planer heraus so dicht wie möglich stehen.
 *
 * **Warum ein Anteil und kein Regler je Figur:** Ein Regler je Figur wäre die vierte
 * Geste auf einem Telefon, auf dem drei schon viel sind (6.2). Ein einziger Wert für die
 * ganze Reihe ist eine Zahl, kein Bedienmodell — und Anna malt die Collage ohnehin
 * selbst; der Planer muss Reihenfolge und ungefähre Dichte treffen, nicht den Pinsel
 * ersetzen.
 */

/**
 * Wie weit zwei Nachbarinnen ineinander ragen.
 *
 * ⚠️ **Aus Annas Produktfoto geschätzt, nicht gemessen:** acht Figuren stehen dort auf
 * rund 210 px Breite, also rund 26 px Abstand von Mitte zu Mitte, bei einer Figurenbreite
 * von grob 30 px. Daraus rund **13 %**. Eine Aufnahme, ein Augenmaß — die Zahl ist ein
 * begründeter Anfangswert, kein Ergebnis. Sie gehört Anna vorgelegt (`TODO.md`).
 */
export const UEBERLAPP_ANTEIL = 0.13;

/**
 * Wie viele Pixel jedes Element nach links über seine Vorgängerin geschoben wird.
 *
 * Gemessen wird am **schmaleren** der beiden Nachbarn, nicht am eigenen Element. Sonst
 * verschlingt ein breites Gruppenbild — drei gemeinsam umfahrene Personen — die
 * Einzelperson daneben: 13 % von einem dreifach breiten Element sind fast eine halbe
 * Figur, 13 % der schmaleren sind, was man sieht.
 *
 * Das erste Element wird nicht geschoben; es gibt nichts, worüber es ragen könnte.
 */
export function ueberlappungen(
  seitenverhaeltnisse: readonly number[],
  hoehe: number,
  anteil: number = UEBERLAPP_ANTEIL,
): number[] {
  return ueberlappungenAusBreiten(
    seitenverhaeltnisse.map((sv) => sv * hoehe),
    anteil,
  );
}

/**
 * Dasselbe, aber aus **fertigen Breiten** statt aus Seitenverhältnissen und einer
 * gemeinsamen Höhe.
 *
 * Gebraucht ab Schritt 4: dort hat jede Figur ihren eigenen Größenfaktor, also ihre
 * eigene Höhe, und eine gemeinsame Zeilenhöhe gibt es nicht mehr. `ueberlappungen`
 * rechnet seitdem hierher — dieselbe Regel an einem Ort, nicht zwei Formeln, die
 * auseinanderlaufen können.
 */
export function ueberlappungenAusBreiten(
  breiten: readonly number[],
  anteil: number = UEBERLAPP_ANTEIL,
): number[] {
  return breiten.map((breite, i) => {
    // Das erste Element hat keine Vorgängerin — `[-1]` ist `undefined`.
    //
    // ⚠️ Diese eine Zeile ist zugleich die **Typ**-Absicherung für
    // `noUncheckedIndexedAccess`; ein Test kann sie nicht eigenständig rot machen, weil
    // es keinen Eingabewert gibt, der sie allein auslöst.
    const vorherige = breiten[i - 1];
    if (vorherige === undefined) return 0;

    // ⚠️ Hier standen bis zur Mutationsprobe am 24.09.2026 **drei** weitere Bremsen:
    // `i === 0`, eine auf `hoehe`/`anteil` und eine auf die schmalere Breite. Alle drei
    // ließen sich löschen, ohne dass ein Test rot wurde — nicht aus Testmangel, sondern
    // weil die Multiplikation in genau diesen Fällen von selbst null ergibt. Vier
    // Zeilen, die aussahen wie Sorgfalt und keine war. Sie sind raus.
    return anteil * Math.min(vorherige, breite);
  });
}

/**
 * Wie viel Breite die Überlappung spart — und damit, wie viel größer die Figuren im
 * Rahmen werden dürfen.
 *
 * Das ist der Teil, der nicht kosmetisch ist. Bei 60 Figuren auf 80 cm ist jede rund
 * 1,3 cm breit; 13 % Überlappung geben rund 15 % Breite zurück, und weil die Höhe an der
 * Breite hängt, werden die Figuren um denselben Anteil größer.
 */
export function gewonneneBreite(anzahl: number, anteil: number = UEBERLAPP_ANTEIL): number {
  if (anzahl < 2 || anteil <= 0) return 0;
  // Ohne Überlappung: `anzahl` Breiten. Mit: eine volle plus (anzahl - 1) gekürzte.
  const ohne = anzahl;
  const mit = 1 + (anzahl - 1) * (1 - anteil);
  return (ohne - mit) / ohne;
}
